import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { isReachable, probeServer, subscribeReachable } from '../api/reachability';
import type { OfflineChange, OfflineEntity, OfflineState, OfflineUpload } from '../offline/engine';
import { useOfflineEngine, useOfflineState } from '../offline/offline';

/**
 * State of the connection and the offline sync for the sync status in the top bar
 * (docs/ui/design-system.md, Shell; ADR-0013): `pending` while changes or files wait for the
 * server, `attention` when a change conflicted or the server refused a change or file and the
 * person has to decide.
 */
export type SyncState = 'synced' | 'syncing' | 'offline' | 'pending' | 'attention';

let lastContact: Date | null = null;
const contactListeners = new Set<() => void>();

/** Called by the query client after every successful answer. */
export function recordServerContact(): void {
  lastContact = new Date();
  for (const listener of contactListeners) listener();
}

function subscribeOnline(onChange: () => void) {
  // Back on the network: ask the server at once instead of waiting for the next probe.
  const online = () => {
    onChange();
    void probeServer();
  };
  window.addEventListener('online', online);
  window.addEventListener('offline', onChange);
  const unsubscribe = subscribeReachable(onChange);
  return () => {
    window.removeEventListener('online', online);
    window.removeEventListener('offline', onChange);
    unsubscribe();
  };
}

function subscribeContact(onChange: () => void) {
  contactListeners.add(onChange);
  return () => contactListeners.delete(onChange);
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine && isReachable(),
    () => true,
  );
}

export interface SyncStatus {
  state: SyncState;
  online: boolean;
  lastContact: Date | null;
  syncNow: () => Promise<void>;
  /** Changes made on the device that wait for the server. */
  pending: number;
  /** Changes that conflicted or were refused. */
  problems: readonly OfflineChange[];
  /** Files on the device that wait for their upload or that the server refused. */
  uploads: readonly OfflineUpload[];
  /** Changes and files that wait for the server, for the count on the status. */
  waiting: number;
  /** What the device holds offline. */
  entities: readonly OfflineEntity[];
  /** Files on the device and records prepared for working offline. */
  storage: OfflineState['storage'] & { prepared: number };
}

export function useSyncStatus(): SyncStatus {
  const client = useQueryClient();
  const online = useOnline();
  const contact = useSyncExternalStore(
    subscribeContact,
    () => lastContact,
    () => null,
  );
  const [manual, setManual] = useState(false);
  const fetching = useIsFetching() > 0;

  const engine = useOfflineEngine();
  const offline = useOfflineState();

  const syncNow = useCallback(async () => {
    setManual(true);
    try {
      await Promise.all([
        client.refetchQueries({ type: 'active' }),
        engine?.sync().catch(() => undefined),
      ]);
    } finally {
      setManual(false);
    }
  }, [client, engine]);

  const pending = offline.changes.filter((c) => c.state === 'pending').length;
  const problems = offline.changes.filter((c) => c.state !== 'pending');
  const refused = offline.uploads.filter((u) => u.state === 'failed').length;
  const waiting = pending + offline.uploads.length - refused;
  const state: SyncState = !online
    ? 'offline'
    : (manual && fetching) || offline.syncing
      ? 'syncing'
      : problems.length || refused
        ? 'attention'
        : waiting
          ? 'pending'
          : 'synced';
  return {
    state,
    online,
    lastContact: contact,
    syncNow,
    pending,
    problems,
    uploads: offline.uploads,
    waiting,
    entities: offline.entities,
    storage: { ...offline.storage, prepared: offline.prepared.length },
  };
}
